<template>
  <div class="flex flex-col gap-3" style="min-height:60vh">
    <div class="flex items-end gap-2">
      <FiltersSelectStyle1
        v-model="statusFilter"
        :label="$t('common.object.status.label')"
        :items="statusOptions"
        :loading="loadingProcesses"
        clear-value="all"
        class="max-w-56"
      />
      <CommonCrudSearchInput v-model="searchTerm" class="flex-1" />
    </div>
    <div class="min-w-0 flex-1">
      <div :class="[menuColorTheme.key]" class="three-ac-table full-height default-cursor bordered" ref="table"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const { t: $t } = useI18n()

import { ref, computed, watch, onMounted } from 'vue'
import type { CellComponent, ColumnDefinition } from 'tabulator-tables'
import type { ExternalProcessSummary } from '~/types/integrations/externalProcess'
import type { Workstation } from '~/types/workstation'

const { theme: menuColorTheme } = useMenuColorTheme();

const emit = defineEmits<{ (e: 'select', process: ExternalProcessSummary): void }>()

const statusFilter = ref('all')

const statusOptions = computed(() => [
  { label: $t('checkr_processes.status.all'), value: 'all' },
  { label: $t('checkr_processes.status.running'), value: 'running' },
  { label: $t('checkr_processes.status.paused'), value: 'paused' },
  { label: $t('checkr_processes.status.complete'), value: 'complete' },
  { label: $t('checkr_processes.status.failed'), value: 'failed' },
  { label: $t('checkr_processes.status.deleted'), value: 'deleted' },
])

const urlWithParams = computed(() => {
  const params = statusFilter.value !== 'all' ? { status: statusFilter.value } : {}
  return `/external-providers/checkr/processes/local?${buildQueryString(params)}`
})

// saveOnCache=false: los estados de los procesos cambian constantemente, no conviene cachear en localStorage.
const { items: processes, loading: loadingProcesses, load: loadProcesses
      } = useCatalogoPlano<ExternalProcessSummary>('catalogo-checkr-local-processes', urlWithParams, undefined, false, false)

const { items: workstationDataResponse, load: loadWorkstations
      } = useCatalogoPlano<Workstation>('catalogo-workstations', '/workstation')

function workstationName(workstationId?: number | null): string {
  if (!workstationId) return '-'
  return workstationDataResponse.value?.find(w => w.id === workstationId)?.name ?? '-'
}

async function manageCheckrProcess(rowData: ExternalProcessSummary, action: 'pause' | 'resume' | 'complete' | 'delete', confirmTitle: string)
{
  const confirmText = action === 'delete'
    ? $t('common.message.delete.prompt_delete')
    : $t('common.message.update.prompt_save')

  const confirmed = await swal_confirm_continue(() => null, confirmTitle, confirmText);
  if (!confirmed) return;

  swal_loading();
  const { data, error } = await useApiFetchObject<{ success: boolean; message: string }>(
    '/external-providers/checkr/processes/manage',
    { method: 'POST', body: { processId: rowData.processId, action } }
  );
  swal_close();

  if (data.value?.success)
  {
    await swal_success(data.value?.message || $t('common.message.update.success'));
    await loadProcesses(true);
  }
  else
  {
    await swal_oops(data.value?.message || error.value?.message || $t('common.message.update.error'));
  }
}

function buildMenuItems(rowData: ExternalProcessSummary)
{
  const status = String(rowData.status ?? '').toLowerCase()
  const items: { id: string; label: string; icon: string; onClick: () => void; class?: string }[] = []

  if (status === 'running')
  {
    items.push({
      id: 'pause', label: $t('common.action.pause'), icon: lucideIcon('pause', 14),
      onClick: () => manageCheckrProcess(rowData, 'pause', $t('common.action.pause'))
    })
  }

  if (status === 'paused')
  {
    items.push({
      id: 'resume', label: $t('common.action.continue'), icon: lucideIcon('play', 14),
      onClick: () => manageCheckrProcess(rowData, 'resume', $t('common.action.continue'))
    })
  }

  if (status === 'running' || status === 'paused')
  {
    items.push({
      id: 'complete', label: $t('common.action.finish'), icon: lucideIcon('circle-check', 14),
      onClick: () => manageCheckrProcess(rowData, 'complete', $t('common.action.finish'))
    })
  }

  if (status !== 'deleted' && status !== 'running')
  {
    if (items.length) items.push({ id: 'sep', label: '', icon: '', onClick: () => {}, class: 'separator' })

    items.push({
      id: 'delete', class:"danger",  label: $t('common.action.delete'), icon: lucideIcon('trash', 14),
      onClick: () => manageCheckrProcess(rowData, 'delete', $t('common.action.delete'))
    })
  }

  return items
}

const statusStyles: Record<string, { bg: string }> = {
  running: { bg: '#0284c7' },
  paused: { bg: '#d97706' },
  complete: { bg: '#359700' },
  failed: { bg: '#d50000' },
  deleted: { bg: '#6b7280' },
}

const columns: ColumnDefinition[] = [
  {
    headerLocaleId: '',
    field: '', visible:false //para evitar el padding left de la columna inicial de toda tabla.
  },
  {
    title: '', maxWidth: 40, minWidth: 40, headerSort: false,
    headerHozAlign: 'center', hozAlign: 'center', frozen: false,
    formatter: () => `
      <button class="bg-transparent cursor-pointer group" style='margin-top:-1px' title="${$t('checkr_processes.select')}">
        <span class="three-ac-table-hover-icon inline-block">
          ${lucideIcon('chevron-left')}
        </span>
      </button>
    `,
    cellClick: (_e: any, cell: CellComponent) => {
      emit('select', cell.getData() as ExternalProcessSummary)
    }
  },
  {
    headerLocaleId: 'checkr_processes.columns.process_id',
    field: 'processId', hozAlign: 'left', minWidth: 90, maxWidth: 90
  },
  {
    headerLocaleId: 'checkr_processes.columns.product',
    field: 'productName', minWidth: 220
  },
  {
    headerLocaleId: 'checkr_processes.columns.workstation',
    field: 'workstationId', minWidth: 150,
    formatter: (cell: CellComponent) => workstationName(cell.getData().workstationId)
  },
  {
    headerLocaleId: 'common.object.status.label',
    field: 'status', hozAlign: 'center', headerHozAlign: 'center', minWidth: 120, maxWidth: 120,
    formatter: (cell: CellComponent) => {
      const status = String(cell.getValue() ?? '').toLowerCase()
      const style = statusStyles[status] ?? { bg: '#6b7280' }
      const knownStatuses = ['running', 'paused', 'complete', 'failed', 'deleted']
      const label = knownStatuses.includes(status) ? $t(`checkr_processes.status.${status}`) : (status || '-')

      return `
        <span class="w-full" style="
          background-color: ${style.bg};
          color: #ffffff;
          font-weight: 600;
          border-radius: 5px;
          padding: 3px 6px 3px 6px;
          display: inline-block;
        ">${label}</span>`
    }
  },
  dateColumn('providerUpdatedAt', 'checkr_processes.columns.updated_at', { frozen: false }),
  {
    title: '', maxWidth: 50, minWidth: 50, headerSort: false,
    headerHozAlign: 'center', hozAlign: 'center', frozen: true,
    formatter: () => `
      <button class="bg-transparent cursor-pointer group" title="${$t('common.action.actions')}">
        <span class="three-ac-table-hover-icon inline-block">
          ${lucideIcon('ellipsis-vertical', 13)}
        </span>
      </button>
    `,
    cellClick: (e: any, cell: CellComponent) => {
      const rowData = cell.getData() as ExternalProcessSummary
      showCellMenu(e, cell, buildMenuItems(rowData), 'right')
    }
  }
]

const { table, searchTerm } = useTabulatorLocalTable({
  columns,
  data: processes,
  persistenceID: 'table-checkr-local-processes',
  layout: 'fitColumns',
})

watch(statusFilter, () => {
  loadProcesses(true)
})

onMounted(async () => {
  await Promise.all([
    loadProcesses(true),
    loadWorkstations()
  ]);
})

defineExpose({ refresh: () => loadProcesses(true) })
</script>
